import React from 'react';
import {
  Site,
  StoreSiteType,
  StoreSiteSection,
  StoreHookupType,
  SiteHookup,
  StoreOverageFee,
  HOOKUP_ICONS,
  HOOKUP_LABELS,
  HookupType,
  OVERAGE_FEE_TYPE_LABELS,
  FEE_FREQUENCY_LABELS,
  OverageFeeType,
  FeeFrequency,
} from '../../types/site';
import { EffectivePricing } from '../../services/siteService';
import { StoreCharge } from '../../services/configService';
import { formatCurrency } from '../../utils/formatters';

interface SiteDetailModalProps {
  site: Site;
  siteTypes: StoreSiteType[];
  sections: StoreSiteSection[];
  hookupTypes: StoreHookupType[];
  siteHookups: SiteHookup[];
  effectivePricing?: EffectivePricing;
  storeOverageFees?: StoreOverageFee[];
  storeCharges?: StoreCharge[];
  detailsLoading?: boolean;
  onClose: () => void;
  onEdit: () => void;
  onMarkMaintenance: () => void;
}

const SiteDetailModal: React.FC<SiteDetailModalProps> = ({
  site,
  siteTypes,
  sections,
  hookupTypes,
  siteHookups,
  effectivePricing,
  storeOverageFees,
  storeCharges,
  detailsLoading = false,
  onClose,
  onEdit,
  onMarkMaintenance,
}) => {
  const siteType = siteTypes.find(t => t.id === site.type_id);
  const section = sections.find(s => s.id === site.site_section_id);
  const hookupTypeMap = new Map(hookupTypes.map(h => [h.id, h]));

  const availableHookups = siteHookups.filter(h => h.available);

  // Filter active store data
  const activeStoreFees = storeOverageFees?.filter(f => f.active) || [];
  const activeStoreCharges = storeCharges?.filter(c => c.active) || [];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="site-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Site {site.alias}</h2>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="detail-section">
            <h3>Basic Information</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">Alias</span>
                <span className="detail-value">{site.alias}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Type Name</span>
                <span className="detail-value">
                  {siteType?.name || 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Section</span>
                <span className="detail-value">{section?.name || 'Not assigned'}</span>
              </div>
              {site.max_length && (
                <div className="detail-item">
                  <span className="detail-label">Max RV Length</span>
                  <span className="detail-value">{site.max_length} ft</span>
                </div>
              )}
              <div className="detail-item">
                <span className="detail-label">Seasonal</span>
                <span className="detail-value">{site.seasonal ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {site.description && (
            <div className="detail-section">
              <h3>Description</h3>
              <p className="site-description">{site.description}</p>
            </div>
          )}

          {site.dimension && (
            <div className="detail-section">
              <h3>Dimensions</h3>
              <div className="detail-grid">
                {site.dimension.length && (
                  <div className="detail-item">
                    <span className="detail-label">Length</span>
                    <span className="detail-value">
                      {site.dimension.length} {site.dimension.unit}
                    </span>
                  </div>
                )}
                {site.dimension.width && (
                  <div className="detail-item">
                    <span className="detail-label">Width</span>
                    <span className="detail-value">
                      {site.dimension.width} {site.dimension.unit}
                    </span>
                  </div>
                )}
                {site.dimension.height && (
                  <div className="detail-item">
                    <span className="detail-label">Height</span>
                    <span className="detail-value">
                      {site.dimension.height} {site.dimension.unit}
                    </span>
                  </div>
                )}
                {site.dimension.note && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Note</span>
                    <span className="detail-value">{site.dimension.note}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="detail-section">
            <h3>Hookups</h3>
            {detailsLoading ? (
              <p className="loading-details">Loading hookups...</p>
            ) : availableHookups.length > 0 ? (
              <div className="hookups-list">
                {availableHookups.map((hookup) => {
                  const type = hookupTypeMap.get(hookup.store_hookup_type_id);
                  const hookupType = type?.hookup_type as HookupType;
                  return (
                    <div key={hookup.id} className="hookup-item">
                      <span className="hookup-icon-large">
                        {HOOKUP_ICONS[hookupType] || '?'}
                      </span>
                      <span className="hookup-name">
                        {type?.display_name || HOOKUP_LABELS[hookupType] || hookupType}
                      </span>
                      {hookup.amp && (
                        <span className="hookup-amp">{hookup.amp}A</span>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="no-hookups">No hookups configured</p>
            )}
          </div>

          {/* Pricing Summary */}
          <div className="detail-section">
            <h3>Pricing</h3>
            {effectivePricing ? (
              <div className="pricing-summary">
                <div className="pricing-main">
                  <span className="pricing-amount">
                    {formatCurrency(effectivePricing.price)}/night
                  </span>
                  <span className={`pricing-source-badge ${effectivePricing.source}`}>
                    {effectivePricing.source === 'store' ? 'Store Default' : 'Site Specific'}
                  </span>
                </div>
                <p className="pricing-description">{effectivePricing.description}</p>
                {effectivePricing.hasSpecialPricing && effectivePricing.specialPricingDateRange && (
                  <p className="pricing-special">
                    Special pricing active: {effectivePricing.specialPricingDateRange}
                  </p>
                )}
              </div>
            ) : (
              <p className="no-pricing">No pricing configured</p>
            )}
          </div>

          {/* Overage Charges Summary */}
          {activeStoreFees.length > 0 && (
            <div className="detail-section">
              <h3>Overage Charges</h3>
              <div className="charges-summary-list">
                {activeStoreFees.map(fee => (
                  <div key={fee.id} className="charge-summary-item">
                    <span className="charge-type-label">
                      {OVERAGE_FEE_TYPE_LABELS[fee.fee_type as OverageFeeType] || fee.fee_type}
                    </span>
                    <span className="charge-detail">
                      {fee.base_included} incl, max {fee.max_allowed} |{' '}
                      {fee.flat ? formatCurrency(fee.amount) : `${fee.amount}%`}{' '}
                      {FEE_FREQUENCY_LABELS[fee.frequency as FeeFrequency] || fee.frequency}
                    </span>
                    <span className="charge-source-badge store">Store</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fees Summary */}
          {activeStoreCharges.length > 0 && (
            <div className="detail-section">
              <h3>Fees</h3>
              <div className="fees-summary-list">
                {activeStoreCharges.map(charge => (
                  <div key={charge.id} className="fee-summary-item">
                    <span className="fee-name-label">{charge.item}</span>
                    <span className="fee-detail">
                      {charge.flat ? formatCurrency(charge.amount) : `${charge.amount}%`}{' '}
                      {FEE_FREQUENCY_LABELS[charge.frequency as FeeFrequency] || charge.frequency}
                    </span>
                    <span className="fee-source-badge store">Store</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onMarkMaintenance}>
            Mark for Maintenance
          </button>
          <button className="btn btn-primary" onClick={onEdit}>
            Edit in Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default SiteDetailModal;
